import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Col, Dropdown, DropdownButton, ProgressBar, Ratio, Row } from 'react-bootstrap'
import { BsHeart, BsHeartFill, BsPlayCircleFill } from 'react-icons/bs'
import ReactPlayer from 'react-player/file'
import Hls, { ErrorTypes } from 'hls.js'
import classnames from 'classnames'

import { useAuth, useRequireLogin } from '../../../Auth'
import { useChange, useFetch, useQueryString, ButtonAsLink } from '../../../Common'
import { useNotifications } from '../../../Notifications'

import { View } from '../../components/View'
import { ComponentHeading } from '../../components/ComponentHeading'
import { Loading } from '../../components/Loading'

import { API_URL, CONTENT_SOURCES } from '../../AppConfig'
import { User } from '../../AppTypes'
import { AuthGetOnetimeToken, ContactContentGetResponse, ContentGetResponse, ContentSources, ContentBase } from '../../../../../cz.vegilove.api/src/_api-models'

type QualityLevels = {
    attrs: {
        RESOLUTION: string
    }
}

type ContentStats = Omit<ContactContentGetResponse, 'modified'>

type ContentData = (
    ContentBase
    & { 
        depLevel: number
        file?: { sourceFile: string, sourceType: ContentSources }, 
        statsIndex?: number 
    }
)

const useVideoQualityLevels = () => {

    const hlsPlayerRef = useRef<Hls>()
    const [ qualityLevels, setQualityLevels ] = useState<string[]>()
    const [ currentLevel, setCurrentLevel ] = useState<number>()

    const onSwitchLevel = ( _ev: unknown, data: { level: number } ) => setCurrentLevel( data.level + 1 )

    const onReady = useCallback( ( player: ReactPlayer ) => {
        hlsPlayerRef.current = player.getInternalPlayer( 'hls' ) as Hls
        if( hlsPlayerRef.current ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const levels = hlsPlayerRef.current.levels as QualityLevels[] || []
            setQualityLevels( [ 'auto', ...levels.map( level => level.attrs.RESOLUTION ) ] )
        }
    }, [] )

    const setLevel = ( level: number ) => {
        if( hlsPlayerRef.current ) hlsPlayerRef.current.currentLevel = level - 1
    }

    useEffect( () => {
        if( hlsPlayerRef.current ) hlsPlayerRef.current.on( Hls.Events.LEVEL_SWITCHED, onSwitchLevel )
        return () => {
            if( hlsPlayerRef.current ) hlsPlayerRef.current.off( Hls.Events.LEVEL_SWITCHED, onSwitchLevel )
        }
    }, [ hlsPlayerRef.current ] )

    return { setQualityLevels: onReady, qualityLevels, setLevel, currentLevel }
}

const useUserStats = () => {

    const auth = useAuth<User>()
    const user = auth.getUser()

    const playerRef = useRef<ReactPlayer | null>( null )

    const [ contentStats, setContentStats ] = useState<ContentStats[]>()
    const [ forceRenderStats, setForceRenderStats ] = useState<boolean>( false )

    useFetch<never, Omit<ContactContentGetResponse, 'modified'>[], ContactContentGetResponse[]>({ 
        // contactId is not provided as API returns always only data for current user
        url: `${ API_URL }/contact-content`,
        label: 'GetContactContentStats',
        //cacheKey: `contact-content-${ user?.contactId || '' }`,
        noAutoRun: !!contentStats || !user?.contactId, // run only once and do not run if user is not known
        // modified need to be removed from data
        onData: data => data.map( d => ({ ...d, modified: undefined }) ),
        onSuccess: state => {
            if( state.success && state.data ) {
                setContentStats( state.data )
            }
        }
    }, [ user ] ) // run immediatly when content is set or changed

    const updateContactContent = useChange<Partial<ContactContentGetResponse>>({ 
        url: API_URL + '/contact-content', 
        label: 'SaveContactContentStats',
        noAbort: true 
    })
    const updateStats = ( stats: { used?: number, favorite?: boolean }, index?: number, contentId?: number ) => {
        // if data not loaded yet - do nothing
        if( !contentStats || !user ) return
        // if neither index nor contentId were provided - do nothing
        if( !index && !contentId ) return
        if( !index && contentId ) {
            // first check if index not exists
            index = getStatsIndex( contentId )
            // when still no stats - add new
            if( !index ) {
                contentStats.push({
                    contentId, contactId: user.contactId,
                    used: stats.used || 0, favorite: stats.favorite || false, rated: null
                })
                index = contentStats.length - 1
            }
        }
        if( index && ( contentStats[ index ].used < 1 || typeof stats.favorite !== 'undefined' ) ) {
            if( typeof stats.used !== 'undefined' ) contentStats[ index ].used = stats.used
            if( typeof stats.favorite !== 'undefined' ) contentStats[ index ].favorite = stats.favorite
            updateContactContent.run({ data: contentStats[ index ] })
            setForceRenderStats( !forceRenderStats )
        }

        // finally return processed stats index index
        return index
    }

    const isFavorite = ( index?: number ) => contentStats && typeof index !== 'undefined' ? contentStats[ index ].favorite : false

    const wasUsed = ( index?: number ) => contentStats && typeof index !== 'undefined' ? contentStats[ index ].used : 0

    const gotoWasUsed = ( index?: number ) => {
        const used = wasUsed( index )
        if( used > 0 && used < 1 ) playerRef.current?.seekTo( used, 'fraction' )
    }

    const getStatsIndex = ( contentId: number ) => {
        // function must return index or undefined -> -1 should not be used!
        let index = -1
        if( contentStats ) index = contentStats.findIndex( cs => cs.contentId === contentId )
        return index > -1 ? index : undefined
    }

    return { playerRef, getStatsIndex, updateStats, gotoWasUsed, wasUsed, isFavorite }
}

export const Video = () => {
    
    const [ videoErrorCount, setVideoErrorCount ] = useState( 0 )
    
    const errorHandler = () => {
        if( videoErrorCount > 5 ) {
            addNotification({ variant: 'danger', category: 'video', content: intl.formatMessage( { description: 'Play.VideoError', defaultMessage: 'Error fetching video. Try another video or try it later, please.' } ) })
        } else setVideoErrorCount( videoErrorCount + 1 )
    }

    const onHlsError = ( error: unknown, data: { type: ErrorTypes } ) => {
        let recoverable = false
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if( error === 'hlsError' && data && typeof data.type !== 'undefined' ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            switch ( data.type ) {
            case Hls.ErrorTypes.NETWORK_ERROR:
                // try to recover network error
                //console.log('fatal network error encountered, try to recover')
                recoverable = true
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                playerRef.current?.getInternalPlayer( 'hls' ).startLoad()
                break
            case Hls.ErrorTypes.MEDIA_ERROR:
                //console.log('fatal media error encountered, try to recover')
                recoverable = true
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                playerRef.current?.getInternalPlayer( 'hls' ).recoverMediaError()
                break
            }
        }
        if( !recoverable || error !== 'hlsError' ) errorHandler()
    }

    const { contentId } = useParams()
    const queryToken = useQueryString( 'token' )
    const [ oneTimeToken, setOnetimeToken ] = useState<string>()
    
    const intl = useIntl()

    const { addNotification } = useNotifications()
    
    const auth = useRequireLogin<User>( !useQueryString( 'token' ) )
    
    const videoRef = useRef<HTMLDivElement>( null )
    const [ chapterIndex, setChapterIndex ] = useState<number>()
    const [ muted, setMuted ] = useState( true )

    // if its auth customer and no OneTimeToken exists - generate one
    const oneTimeTokenRequest = useFetch<AuthGetOnetimeToken>({
        url: `${ API_URL }/auth/getOnetimeToken${ queryToken ? `?token=${ queryToken }` : '' }`,
        label: 'GetOnetimeToken',
        noAbort: !!oneTimeToken, // if oneTimeToken exists - do not generate
        onSuccess: ( state ) => setOnetimeToken( state.data?.token )
    })
    
    const { state: contentState } = useFetch<never, ContentData[], ContentGetResponse[]>({
        url: `${ API_URL }/content/${ contentId || '' }${ !auth.isAuthenticated && oneTimeToken ? '?token=' + oneTimeToken : '' }`, 
        label: 'GetContent',
        cacheKey: contentId ? `content-${ contentId }` : undefined /* will never happen */,
        noAutoRun: ( !oneTimeToken ) || !contentId,
        onData: data => {
            const normalizeData = ( doc: ContentBase, depLevel: number ): ContentData => {
                const file = doc.attachments?.find( attn => attn.type === 'content' )
                return { 
                    ...doc,
                    depLevel,
                    file: file ? {
                        sourceFile: file.source === 'aws-private' && file.filename.includes( '.m3u8' )
                            ? `${ API_URL }/aws/signPlaylist${ file?.filename }?token=${ oneTimeToken || '' }`
                            : CONTENT_SOURCES[ file.source ] + file?.filename
                        ,
                        sourceType: file?.source,
                    } : undefined,
                    statsIndex: getStatsIndex( doc.contentId )
                }
            }
            if( data.length === 1 ) {
                // first add the main source
                const newData = [ normalizeData( data[ 0 ], 0 ) ]
                if( data[ 0 ].chapters ) data[ 0 ].chapters.forEach( doc => newData.push( normalizeData( doc, doc.depLevel + 1 ) ) )
                return newData
            }
            return []
        },
        onSuccess: ({ data }) => {
            if( data && data?.length > 0 ) setChapterIndex( data.findIndex( c => c.file ) )
        }
    }, [ contentId, oneTimeToken ] )
    
    const selectChapter = ( index: number ) => {
        if( contentState?.data ) {
            let nextChapterIndex = index
            while( !contentState.data[ nextChapterIndex ]?.file ) {
                nextChapterIndex++
                if( nextChapterIndex > contentState.data.length - 1 ) nextChapterIndex = 0
            }
            setChapterIndex( nextChapterIndex )
            setVideoErrorCount( 0 )
            if( videoRef.current ) videoRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }

    const { setQualityLevels, qualityLevels, setLevel, currentLevel } = useVideoQualityLevels()
    const { playerRef, getStatsIndex, updateStats, gotoWasUsed, wasUsed, isFavorite } = useUserStats()

    const mainDoc = useMemo( () => contentState?.data && contentState?.data.length > 0 ? contentState?.data[ 0 ] : undefined, [ contentState ] )
    const currentChapter = contentState?.data && typeof chapterIndex !== 'undefined' ? contentState?.data[ chapterIndex ] : undefined

    return <View>
        <ComponentHeading>
            <FormattedMessage description='App.Video.Title' defaultMessage='Online course: {name}' values={{ name: mainDoc?.name }} />
        </ComponentHeading>
        <Loading show={ oneTimeTokenRequest.state.processing || contentState.processing } />
        <Row>
            <Col size={ 12 } md={ 6 }>
                <Ratio aspectRatio="16x9" ref={ videoRef } style={{ border: 'solid 1px #ddd' }}><>
                    { !currentChapter && <>
                        <div style={{ background: 'black', display: 'flex', justifyContent: 'center' }}><div className='loader' /></div> 
                        <Loading show />
                    </>}
                    { currentChapter && currentChapter.file && <ReactPlayer
                        key={ String( currentChapter.contentId ) }
                        ref={ playerRef }
                        url={ currentChapter.file.sourceFile }
                        playing
                        muted={ muted }
                        width='100%'
                        height='100%'
                        controls
                        pip={ false }
                        onReady={ player => {
                            setQualityLevels( player as ReactPlayer ) 
                        } }
                        onDuration={ () => gotoWasUsed( currentChapter.statsIndex ) }
                        progressInterval={ 10 * 1000 } // milliseconds - set granularity of registered progress
                        onStart={ () => { if( muted ) setMuted( false ) } }
                        onProgress={ ({ played }) => { currentChapter.statsIndex = updateStats({ used: played }, currentChapter.statsIndex, currentChapter.contentId )} }
                        onEnded={ () => { 
                            currentChapter.statsIndex = updateStats({ used: 1 }, currentChapter.statsIndex, currentChapter.contentId)
                            if( chapterIndex ) selectChapter( chapterIndex + 1 ) 
                        }}
                        onError={ onHlsError }
                        config={{
                            attributes: {
                                crossOrigin: 'anonymous',
                                playsInline: true,
                                disablePictureInPicture: true
                            },
                            hlsVersion: '1.3.2',
                        }}
                    /> }
                </></Ratio>
                <div className='mt-1 d-flex'>
                    <div className='d-inline-block me-auto'>{ qualityLevels && qualityLevels.length > 1 && 
                        <DropdownButton variant="success" size='sm' title={ intl.formatMessage({ description: 'App.Video.Quality', defaultMessage: 'Quality' }) + ': ' + ( typeof currentLevel === 'number' ? qualityLevels[ currentLevel ] : 'n/a' ) }>
                            { qualityLevels.map( ( level, index ) => <Dropdown.Item key={ index } href="#" onClick={ () => setLevel( index ) }>{ level }</Dropdown.Item> ) }
                        </DropdownButton> 
                    } </div>
                    <div className='d-inline-block text-end'><ButtonAsLink onClick={ () => updateStats({ favorite: !isFavorite( currentChapter?.statsIndex ) }, currentChapter?.statsIndex, currentChapter?.contentId ) }>{ isFavorite( currentChapter?.statsIndex ) ? <BsHeartFill color='red' /> : <BsHeart color='red' />}</ButtonAsLink></div>
                </div>
            </Col>
            <Col size={ 12 } md={ 6 }>
                { mainDoc?.description && <div className='mt-4 p-1 p-md-0' dangerouslySetInnerHTML={{ __html: mainDoc.description || '' }}></div> }
                { contentState?.data && contentState.data.map( ( chapter, index ) => {
                    // ignore main document
                    if( chapter.depLevel === 0 ) return
                    // fill stats index if not exists
                    if( !chapter.statsIndex ) chapter.statsIndex = getStatsIndex( chapter.contentId )
                    const clickable = !!chapter.file
                    const wrapperCls = classnames( [ 'chapter-wrapper d-flex', chapter.depLevel === 1 ? 'pt-3 fw-bold' : '' ] )
                    const used = wasUsed( chapter.statsIndex )
                    return <div key={ chapter.contentId } className={ wrapperCls }>
                        <div style={{ width: '30px', minWidth: '30px' }}>{ index === chapterIndex ? <BsPlayCircleFill /> : '' }</div>
                        <div>
                            <div>
                                { clickable 
                                    ? <ButtonAsLink onClick={ () => selectChapter( index ) }>{ chapter.name }</ButtonAsLink>
                                    : <span>{ chapter.name }</span>
                                }
                                { isFavorite( chapter.statsIndex ) ? <span>&nbsp;<BsHeartFill color='red' /></span> : '' }
                            </div>
                            { used > 0 && <div className='w-100'><ProgressBar style={{ height: '2px' }} now={ used * 100 || 0 } label={`${ used * 100 || 0 }%`} visuallyHidden /></div> }
                        </div>
                    </div>
                })}
            </Col>
        </Row>
    </View>
}

export default Video
